import type { Document, Manifest } from '../api/authoring'
import { previewOf } from './preview'

// What one component is called, on a screen that is looking at it.
//
// **Build mode used to answer this with an id**, and content mode never
// did. A heading reading `5526205d-f386-4efa-8fdf-c5e91d8c1b0a`, a
// breadcrumb reading it again, and a placement row reading `→` and then
// a second one: all true, none of them the answer to "which component am
// I looking at". The library the operator arrived from had the name in
// the row they clicked.
//
// The order below is the whole of it, and each step is somebody else's
// decision rather than this file's:
//
//  1. **What the type says it is called.** `label` is derived by the
//     platform from the declaration the type itself makes — a Page is
//     named by its title, a Website by its, a tenant's own type by
//     whichever field it declared. It is the same reading a listing row
//     carries, which is what makes a library and the screen it opens
//     agree.
//  2. **The first words it holds**, for a type that declares no name at
//     all. That is `previewOf`, the reading content mode already draws
//     every block row from, and it is what puts a headline on a section
//     nobody has labelled.
//  3. **Nothing**, and the caller says what to show instead — its id, or
//     the type's own prose. A Container holding two blocks and no words
//     genuinely has no name, and inventing one would be worse than the
//     id: two of them would invent the same one.
//
// Nothing here names a type, and no step is a rule this client invented
// about which field means what.

/**
 * The name a component calls itself by, or undefined where it has none.
 *
 * Undefined rather than a fallback string, because what to show in its
 * place is the caller's: a heading shows the id, a row in a tree shows
 * what the type is called, and neither can be decided here.
 */
export function nameOf(document: Document | undefined, manifest?: Manifest): string | undefined {
  if (!document) return undefined
  if (document.label) return document.label
  const said = previewOf(manifest, document.type, (document.fields ?? {}) as Record<string, unknown>)
  return said[0]
}

/**
 * The same name, with the component's id where there is none.
 *
 * The heading form: a screen looking at one component must always say
 * *something*, and the id is what it has. Callers draw the id beside the
 * name in any case, so a heading that falls back to it repeats a chip
 * rather than losing a fact.
 */
export function nameOrId(document: Document | undefined, manifest?: Manifest): string {
  return nameOf(document, manifest) ?? document?.id ?? ''
}
